import { Schedule } from "@/pages/human-resources/time-management/business/schedule/Schedule.ts";
import {
  mapToSortedTimeSlots,
  type TimeSlotOut,
} from "@/pages/human-resources/time-management/endpoints/schedules/inout/TimeSlotOut.ts";
import { parseIsoDateTime } from "@/shared/utils/Dates.ts";
import {
  type MetaDataOut,
  mapToMetaData,
} from "@/pages/human-resources/time-management/endpoints/inout/EditionMetaDataOut.ts";
import {
  mapToScheduleDailyTag,
  type ScheduleDailyTagOut,
} from "@/pages/human-resources/time-management/endpoints/schedules/inout/ScheduleDailyTagOut.ts";
import type { ScheduleState } from "@/pages/human-resources/time-management/business/schedule/ScheduleState.ts";
import type { AchievedScheduleValidationLevel } from "@/pages/human-resources/time-management/business/schedule/AchievedScheduleValidationLevel.ts";
import type { ScheduleReferenceData } from "@/pages/human-resources/time-management/business/reference/ScheduleReferenceData.ts";
import { defined } from "@/shared/utils/makeDefined.ts";

export type ScheduleOut = MetaDataOut & {
  id: number;
  draftScheduleId: number | undefined;
  achievedValidationLevel: number | undefined;
  siteId: number;
  employeeId: number;
  date: string;
  label: string | undefined;
  comment: string | undefined;
  state: ScheduleState;
  color: string | undefined;
  nbMeals: number | undefined;
  timeSlots: TimeSlotOut[] | undefined;
  dailyTags: ScheduleDailyTagOut[] | undefined;
  valorizedWorkMinutes: number | undefined;
};

export function mapToSchedules(
  refData: ScheduleReferenceData,
  schedulesOut: ScheduleOut[],
): Schedule[] {
  return schedulesOut.map((dto) => {
    return mapToSchedule(refData, dto);
  });
}

function mapToSchedule(
  refData: ScheduleReferenceData,
  so: ScheduleOut,
): Schedule {
  const currentSiteId = refData.siteData.siteId;
  const mandatoryArgs = {
    siteId: currentSiteId,
    employeeId: so.employeeId.toString(),
    date: parseIsoDateTime(so.date),
  };
  const achievedValidationLevel = mapAchievedValidationLevel(
    so.achievedValidationLevel,
  );

  const dtoSiteId = so.siteId.toString();
  if (dtoSiteId !== currentSiteId) {
    // MAD case
    return new Schedule(mandatoryArgs, {
      publishedId: so.id.toString(),
      modified: false,
      achievedValidationLevel,
      otherSiteId: dtoSiteId,
      timeSlots: mapToSortedTimeSlots(
        refData.domainData.hourlyTagsResolver,
        extractTimeSlots(so),
      ),
      dailyTags: (so.dailyTags ?? []).map((t) =>
        mapToScheduleDailyTag(refData.dailyTagsResolver, t),
      ),
      valorizedWorkMinutes: so.valorizedWorkMinutes,
    });
  }
  if (isDeletedDraft(so)) {
    return new Schedule(mandatoryArgs, {
      publishedId: so.id.toString(),
      modified: true,
      achievedValidationLevel,
      valorizedWorkMinutes: so.valorizedWorkMinutes,
    });
  } else if (isAchievedOnly(so)) {
    return new Schedule(mandatoryArgs, { achievedValidationLevel });
  } else {
    return new Schedule(mandatoryArgs, {
      ...so,
      id: extractDraftId(so),
      modified: isModified(so),
      achievedValidationLevel,
      timeSlots: mapToSortedTimeSlots(
        refData.domainData.hourlyTagsResolver,
        extractTimeSlots(so),
      ),
      dailyTags: (so.dailyTags ?? []).map((t) =>
        mapToScheduleDailyTag(refData.dailyTagsResolver, t),
      ),
      valorizedWorkMinutes: so.valorizedWorkMinutes,
      metaData: mapToMetaData(so),
    });
  }
}

function isDeletedDraft(so: ScheduleOut): boolean {
  return so.state === "PUBLISHED" && so.draftScheduleId === undefined;
}

function isAchievedOnly(so: ScheduleOut): boolean {
  return so.state === "ACHIEVED" && so.draftScheduleId === undefined;
}

function extractDraftId(so: ScheduleOut): string {
  let id = undefined;
  if (so.state === "DRAFT") id = so.id;
  else if (so.state === "PUBLISHED") id = so.draftScheduleId;
  return defined(id, "Non deleted draft schedules must have an id").toString();
}

function isModified(so: ScheduleOut): boolean {
  return so.draftScheduleId === undefined;
}

function extractTimeSlots(so: ScheduleOut): TimeSlotOut[] {
  const timeSlots = so.timeSlots ?? [];
  if (so.state === "PUBLISHED") {
    // Clear timeslot ids upfront to prevent errors when updating a published schedule.
    timeSlots.forEach((ts) => {
      ts.id = undefined;
    });
  }
  return timeSlots;
}

function mapAchievedValidationLevel(
  intValue: number | undefined,
): AchievedScheduleValidationLevel | undefined {
  switch (intValue) {
    case undefined:
      return undefined;
    case 0:
      return "NONE";
    case 1:
      return "TEMPORARY";
    case 2:
      return "DEFINITIVE";
    default:
      throw new Error(
        `Unexpected achieved schedule validation level: ${intValue}`,
      );
  }
}
